import { useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { isAllCompaniesSelectionActive } from 'utils/allCompaniesMode';
import { ROUTES, URL_PARAM_KEYS } from 'utils/constants';
import {
  ensureAliasForCompanyId,
  getCompanyIdByAlias,
} from 'utils/activeCompanyStorageUtils';
import { peekSuperDashboardDrilldownTarget } from 'utils/superDashboardReturn';

export function useCompanyAliasUrlSync(): void {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const availableCompanies = useActiveCompanyStore((s) => s.availableCompanies);
  const isInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const setActiveCompanyId = useActiveCompanyStore((s) => s.setActiveCompanyId);
  const lastSeenAliasRef = useRef<string>('');

  useEffect(() => {
    if (location.pathname === ROUTES.RECEIPT_IMAGE_TAB) return;
    if (location.pathname === ROUTES.COMING_SOON_HTML) return;
    if (!isInitialized) return;
    if (isAllCompaniesSelectionActive(activeCompanyId)) return;
    if (peekSuperDashboardDrilldownTarget()) return;

    const aliasParamKey = URL_PARAM_KEYS?.COMPANY_ALIAS ?? 'co';
    const aliasFromUrl = searchParams.get(aliasParamKey)?.trim() ?? '';
    const orderedCompanyIds = availableCompanies.map((company) => company.id);
    const availableCompanyIds = new Set(orderedCompanyIds);

    const didAliasChangeFromNavigation =
      aliasFromUrl !== lastSeenAliasRef.current;

    if (aliasFromUrl && didAliasChangeFromNavigation) {
      const companyIdFromAlias = getCompanyIdByAlias(
        aliasFromUrl,
        orderedCompanyIds,
      );
      const isResolvedCompanyValid =
        !!companyIdFromAlias && availableCompanyIds.has(companyIdFromAlias);

      if (isResolvedCompanyValid && companyIdFromAlias !== activeCompanyId) {
        lastSeenAliasRef.current = aliasFromUrl;
        void setActiveCompanyId(companyIdFromAlias);
        return;
      }
    }

    if (!activeCompanyId || !availableCompanyIds.has(activeCompanyId)) return;

    const aliasForActiveCompany = ensureAliasForCompanyId(
      activeCompanyId,
      orderedCompanyIds,
    );
    if (aliasFromUrl === aliasForActiveCompany) {
      lastSeenAliasRef.current = aliasFromUrl;
      return;
    }

    lastSeenAliasRef.current = aliasForActiveCompany;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(aliasParamKey, aliasForActiveCompany);
        return next;
      },
      { replace: true },
    );
  }, [
    isInitialized,
    location.pathname,
    searchParams,
    activeCompanyId,
    availableCompanies,
    setSearchParams,
    setActiveCompanyId,
  ]);
}
